// Import model
import { ObjectAppointment } from '../models/ObjectAppointment';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { Address } from '../models/Address';

// Import dependency serializers (for complex types)
import { AddressSerializer } from './AddressSerializer';

// Import derived type serializers (for polymorphic deserialization)
import { ContactAppointmentSerializer } from './ContactAppointmentSerializer';
import { LeadAppointmentSerializer } from './LeadAppointmentSerializer';
import { ProjectAppointmentSerializer } from './ProjectAppointmentSerializer';

/**
 * Serializer for ObjectAppointment - handles JSON serialization and deserialization with type validation
 */
export class ObjectAppointmentSerializer {
  /**
   * Deserializes a JSON string into a ObjectAppointment instance
   */
  static deserialize(json: string): ObjectAppointment {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a ObjectAppointment instance
   */
  static fromObject(obj: any): ObjectAppointment {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to ObjectAppointment');
    }

    // Handle polymorphic deserialization using discriminator
    const discriminator = obj['parentObjectType'];

    switch (discriminator) {
      case 'Contact':
        return ContactAppointmentSerializer.fromObject(obj);
      case 'Lead':
        return LeadAppointmentSerializer.fromObject(obj);
      case 'salesforce.WorkOrder':
        return ProjectAppointmentSerializer.fromObject(obj);
      default:
        // Fall through to base type deserialization
        break;
    }

    const instance: ObjectAppointment = {
      parentObjectType: obj['parentObjectType'] as string,
      parentObjectTypeName: obj['parentObjectType|Name'] as string | undefined,
      address: obj['address'] ? AddressSerializer.fromObject(obj['address']) : undefined as any,
      endDate: obj['endDate'] as string | undefined,
      isAllDay: obj['isAllDay'] as boolean | undefined,
      startDate: obj['startDate'] as string | undefined,
      timeZoneId: obj['timeZoneId'] as string | undefined,
      organizationId: obj['organizationId'] as string | undefined,
      organizationIdName: obj['organizationId|Name'] as string | undefined,
      o365eventId: obj['o365EventId'] as string | undefined,
      o365eventIdName: obj['o365EventId|Name'] as string | undefined,
      salesforceAppointmentId: obj['salesforceAppointmentId'] as string | undefined,
      salesforceAppointmentIdName: obj['salesforceAppointmentId|Name'] as string | undefined,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      createdBy: obj['createdBy'] as string | undefined,
      createdByName: obj['createdBy|Name'] as string | undefined,
      lastModifiedDate: obj['lastModifiedDate'] as string | undefined,
      content: obj['content'] as string | undefined,
      contentType: obj['contentType'] as string | undefined,
      creatorId: obj['creatorId'] as string | undefined,
      creatorIdName: obj['creatorId|Name'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      relatedObjects: obj['relatedObjects'] as string | undefined,
      parentObjectId: obj['parentObjectId'] as string | undefined,
      appointmentTypeId: obj['appointmentTypeId'] as string | undefined,
      appointmentTypeIdName: obj['appointmentTypeId|Name'] as string | undefined,
      cancelledByUserId: obj['cancelledByUserId'] as string | undefined,
      cancelledByUserIdName: obj['cancelledByUserId|Name'] as string | undefined,
      cancelledDate: obj['cancelledDate'] as string | undefined,
      replacedById: obj['replacedById'] as string | undefined,
      replacedByIdName: obj['replacedById|Name'] as string | undefined,
      tool: obj['tool'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of ObjectAppointment instances
   */
  static deserializeArray(json: string): ObjectAppointment[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a ObjectAppointment instance to a JSON string
   */
  static serialize(instance: ObjectAppointment): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a ObjectAppointment instance to a plain object
   */
  static toObject(instance: ObjectAppointment): any {
    return {
      'parentObjectType': instance.parentObjectType,
      'parentObjectType|Name': instance.parentObjectTypeName,
      'address': instance.address ? AddressSerializer.toObject(instance.address) : undefined,
      'endDate': instance.endDate,
      'isAllDay': instance.isAllDay,
      'startDate': instance.startDate,
      'timeZoneId': instance.timeZoneId,
      'organizationId': instance.organizationId,
      'organizationId|Name': instance.organizationIdName,
      'o365EventId': instance.o365eventId,
      'o365EventId|Name': instance.o365eventIdName,
      'salesforceAppointmentId': instance.salesforceAppointmentId,
      'salesforceAppointmentId|Name': instance.salesforceAppointmentIdName,
      'name': instance.name,
      'description': instance.description,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'createdBy': instance.createdBy,
      'createdBy|Name': instance.createdByName,
      'lastModifiedDate': instance.lastModifiedDate,
      'content': instance.content,
      'contentType': instance.contentType,
      'creatorId': instance.creatorId,
      'creatorId|Name': instance.creatorIdName,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      'isActive': instance.isActive,
      'relatedObjects': instance.relatedObjects,
      'parentObjectId': instance.parentObjectId,
      'appointmentTypeId': instance.appointmentTypeId,
      'appointmentTypeId|Name': instance.appointmentTypeIdName,
      'cancelledByUserId': instance.cancelledByUserId,
      'cancelledByUserId|Name': instance.cancelledByUserIdName,
      'cancelledDate': instance.cancelledDate,
      'replacedById': instance.replacedById,
      'replacedById|Name': instance.replacedByIdName,
      'tool': instance.tool,
    };
  }

  /**
   * Serializes an array of ObjectAppointment instances to a JSON string
   */
  static serializeArray(instances: ObjectAppointment[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
